import * as XLSX from 'xlsx';
import { numberToIndianWords } from './numberToWords.js';

/**
 * Automatically inspects and parses an uploaded Excel file (.xlsx, .xlsm)
 * into a full Rajput Security Services bill configuration.
 *
 * Supported formats:
 * 1. SVNIT 7-Column Housekeeping & Disinfection (MAY-26 BILL.xlsm)
 * 2. BAOU GeM Facility & Sanitation (INVOICE - Copy.xlsm)
 * 3. Version 2.0 Security Service (INVOICE.xlsx, JULY-26.xlsx)
 * 4. Standard Security Manpower (SRFDCL format)
 */
export function parseUploadedExcel(fileBuffer, fileName = 'Uploaded Bill') {
  const wb = XLSX.read(fileBuffer, { type: 'array', cellDates: true });
  const firstSheetName = wb.SheetNames[0];
  const sheet = wb.Sheets[firstSheetName];
  const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });

  // Convert all rows into a flat search string to detect format
  const fullText = rows
    .map((r) => r.map((c) => String(c)).join(' '))
    .join(' ')
    .toUpperCase();

  const isSVNIT =
    fullText.includes('SVNIT') ||
    (fullText.includes('FREQUENCY') && fullText.includes('WORKING DAYS'));

  const isBAOU =
    fullText.includes('AMBEDAKAR') ||
    fullText.includes('AMBEDKAR') ||
    fullText.includes('BAOU') ||
    fullText.includes('CONSUMABLES/EQUIPMENTS') ||
    (fullText.includes('GEM CONTRACT') && fullText.includes('MANDAYS'));

  const isSecurityV2 =
    fullText.includes('VERSION 2.0') ||
    fullText.includes('PRESENT DAYS IN A MONTH') ||
    fullText.includes('MINIMUM DAILY WAGES');

  if (isSVNIT) {
    return parseSVNITExcel(rows, fileName);
  } else if (isBAOU) {
    return parseBAOUExcel(rows, fileName);
  } else if (isSecurityV2) {
    return parseSecurityV2Excel(rows, fileName);
  }

  try {
    return parseStandardExcel(rows, fileName);
  } catch {
    return parseGenericExcel(rows, fileName);
  }
}

function parseGenericExcel(rows, fileName) {
  const cleaned = rows
    .map((row) => row.map((cell) => (cell instanceof Date ? formatDate(cell) : String(cell ?? '').trim())))
    .filter((row) => row.some(Boolean));

  let width = 0;
  cleaned.forEach((row) => {
    for (let i = row.length - 1; i >= 0; i--) {
      if (row[i]) {
        width = Math.max(width, i + 1);
        break;
      }
    }
  });
  width = Math.min(Math.max(width, 1), 8);

  const sheetRows = cleaned.slice(0, 40).map((row) => {
    const next = row.slice(0, width);
    while (next.length < width) next.push('');
    return next;
  });

  const headerIndex = sheetRows.findIndex((row) => row.filter((cell) => cell && Number.isNaN(Number(cell))).length >= 3);
  const headers = headerIndex >= 0 ? sheetRows[headerIndex] : sheetRows[0] || [];
  const clientLine = sheetRows.find((row) => row[0] && !/^(to,|sr\.|note)/i.test(row[0]));
  const companyName = clientLine?.[0] || fileName.replace(/\.[^/.]+$/, '') || 'Imported bill';

  return {
    name: companyName.slice(0, 80),
    description: `Imported Excel layout from ${fileName}. Reuse this template for the same client format.`,
    formatType: 'imported',
    isDefault: false,
    config: {
      formatType: 'imported',
      letterhead: {
        enabled: true,
        mode: 'digital',
        topMarginMm: 38,
        bottomMarginMm: 26,
        sideMarginMm: 8,
        showWatermark: true,
        opacity: 100,
      },
      recipient: { companyName, toTitle: 'TO,', designation: '', addressLines: [], gstin: '' },
      invoiceMeta: { invoiceNo: fileName.replace(/\.[^/.]+$/, '') },
      billingMonthTitle: '',
      sheet: { headers, rows: sheetRows },
      notes: [],
      signatory: { companyName: 'For, Rajput Security service', signatoryTitle: 'Authorised Signatory' },
      footer: { leftNotice: 'E & O.E.', disclaimer: "This is a computer-generated invoice doesn't require any signature." },
    },
  };
}

/**
 * Parser for SVNIT Surat 7-Column Format
 */
function parseSVNITExcel(rows, fileName) {
  let toTitle = 'To,';
  let designation = 'The Director,';
  let companyName = 'SVNIT SURAT, National Institute of Technology (NIT)';
  const addressLines = [];
  let gstin = '';
  let workDescription = 'Work: Housekeeping, Sanitation and disinfection services at SVNIT Campus, Surat';

  let invoiceNo = 'RSS/000/2026-27';
  let invoiceDate = '';
  let contractNo = 'GEMC-511687719261646';
  let generatedDate = '';
  let sacCode = '9985';
  let billingMonth = '2026-05-01';
  let billingPeriod = '01-05-2026 To 31-05-2026';

  let bankName = 'The Karnavati Co-op Bank Ltd.';
  let branchName = 'NIKOL ROAD, AHMEDABAD';
  let accountNo = '124003005002936';
  let ifscCode = 'GSCB0UTKCBL';
  let panNo = 'AAOFR9854P';
  let companyGstin = '24AAOFR9854P1Z4';

  const items = [];
  let tableStarted = false;

  for (let r = 0; r < rows.length; r++) {
    const row = rows[r];
    const rowStr = row.join(' ').toUpperCase();

    // Recipient extraction
    if (row[0] && String(row[0]).trim().toUpperCase() === 'TO,') {
      toTitle = String(row[0]).trim();
    }
    if (r === 1 && row[0]) designation = String(row[0]).trim();
    if (r === 2 && row[0]) companyName = String(row[0]).trim();
    if ((r === 3 || r === 4 || r === 5) && row[0]) {
      const line = String(row[0]).trim();
      if (!line.toUpperCase().startsWith('GSTIN') && !line.toUpperCase().startsWith('WORK')) {
        addressLines.push(line);
      }
    }
    if (rowStr.includes('GSTIN:')) {
      const match = rowStr.match(/GSTIN:\s*([A-Z0-9]+)/);
      if (match) gstin = match[1];
    }
    if (rowStr.includes('WORK:')) {
      workDescription = String(row[0] || '').trim();
    }

    // Invoice meta extraction (right side)
    for (let c = 0; c < row.length; c++) {
      const val = String(row[c]).trim().toUpperCase();
      const nextVal = row[c + 1] !== undefined ? String(row[c + 1]).trim() : '';
      const farVal = row[c + 3] !== undefined ? String(row[c + 3]).trim() : '';
      const targetVal = farVal || nextVal;

      if (val.includes('INVOICE NO.')) {
        if (targetVal) invoiceNo = targetVal;
      } else if (val === 'DATE:') {
        if (targetVal) invoiceDate = formatDate(targetVal);
      } else if (val.includes('CONTRACT NO.')) {
        if (targetVal) contractNo = targetVal;
      } else if (val.includes('GENERATED DATE:')) {
        if (targetVal) generatedDate = formatDate(targetVal);
      } else if (val.includes('SAC CODE NO.')) {
        if (targetVal) sacCode = targetVal;
      } else if (val.includes('BILLING MONTH')) {
        if (targetVal) billingMonth = formatDate(targetVal);
      } else if (val.includes('BILLING PERIOD')) {
        if (targetVal) billingPeriod = targetVal;
      } else if (val === 'BANK') {
        if (nextVal) bankName = nextVal;
      } else if (val === 'A/C NO.') {
        if (nextVal) accountNo = nextVal;
      } else if (val === 'IFSC CODE') {
        if (nextVal) ifscCode = nextVal;
      } else if (val === 'BRANCH') {
        if (nextVal) branchName = nextVal;
      } else if (val === 'PAN') {
        if (nextVal) panNo = nextVal;
      } else if (val === 'GSTIN') {
        if (nextVal) companyGstin = nextVal;
      }
    }

    // Table item rows
    if (rowStr.includes('SR.') && rowStr.includes('DESCRIPTION')) {
      tableStarted = true;
      continue;
    }
    if (tableStarted && (rowStr.includes('TOTAL AMOUNT') || rowStr.includes('IN WORDS'))) {
      tableStarted = false;
    }
    if (tableStarted) {
      const sr = Number(row[0]);
      if (!isNaN(sr) && sr > 0 && row[1]) {
        const qty = Number(row[2]) || 0;
        const unitPrice = Number(row[3]) || 0;
        const freq = Number(row[4]) || 1;
        const days = Number(row[5]) || 0;
        const amt = Number(row[6]) || qty * unitPrice * freq * days;

        items.push({
          id: `item-${items.length + 1}`,
          srNo: sr,
          particular: String(row[1]).trim(),
          quantity: qty,
          unitPrice: unitPrice,
          frequency: freq,
          workingDays: days,
          totalAmount: amt,
        });
      }
    }
  }

  return {
    name: fileName.replace(/\.[^/.]+$/, '') || 'SVNIT Housekeeping Bill',
    description: 'Imported SVNIT 7-column multi-cycle bill format with Reverse GST calculation.',
    formatType: 'svnit_frequency',
    isDefault: false,
    config: {
      formatType: 'svnit_frequency',
      recipient: {
        toTitle: toTitle || 'To,',
        designation: designation || 'The Director,',
        companyName: companyName || 'SVNIT SURAT, National Institute of Technology (NIT)',
        addressLines: addressLines.length > 0 ? addressLines : [
          'SVNIT Campus Dumas Road, SURAT,',
          'GUJARAT-395007, India'
        ],
        gstin: gstin || '24AAAJS1184P1ZP',
        workDescription: workDescription,
      },
      invoiceMeta: {
        invoicePrefix: invoiceNo.split('/')[0] || 'RSS',
        invoiceNo: invoiceNo || 'RSS/000/2026-27',
        invoiceDate: invoiceDate || '29/06/2026',
        contractNo: contractNo || 'GEMC-511687719261646',
        generatedDate: generatedDate || '24/02/2026',
        sacCode: sacCode || '9985',
        billingMonthTitle: billingMonth || '2026-05-01',
        billingPeriod: billingPeriod || '01-05-2026 To 31-05-2026',
      },
      billingMonthTitle: billingMonth || '2026-05-01',
      items: items.length > 0 ? items : [
        {
          id: '1',
          srNo: 1,
          particular: 'General Cleaning (Sweeping, Mopping, Dusting); Outdoor',
          quantity: 72010,
          unitPrice: 1,
          frequency: 1,
          workingDays: 0,
          totalAmount: 0,
        },
      ],
      taxScheme: 'reverse_gst_inclusive',
      reverseGstFactor: 0.847457627118644,
      companyTaxInfo: {
        gstin: companyGstin || '24AAOFR9854P1Z4',
        panNo: panNo || 'AAOFR9854P',
      },
      bankDetails: {
        bankName: bankName || 'The Karnavati Co-op Bank Ltd.',
        branchName: branchName || 'NIKOL ROAD, AHMEDABAD',
        accountNo: accountNo || '124003005002936',
        ifscCode: ifscCode || 'GSCB0UTKCBL',
      },
      notes: [
        '1 Subject to Ahmedabad Jurisdiction.',
        '2 This bill is to be paid by 7th of currents months.',
        '3 All Calculations are on the basis of an English calendar months.',
      ],
      signatory: {
        companyName: 'For, Rajput Security service',
        signatoryTitle: 'Authorised Signatory',
      },
      footer: {
        leftNotice: 'E & O.E.',
        disclaimer: "This is a computer-generated invoice doesn't require any signature.",
      },
    },
  };
}

/**
 * Parser for BAOU Open University Format (INVOICE - Copy.xlsm)
 */
function parseBAOUExcel(rows, fileName) {
  let toTitle = 'TO,';
  let companyName = 'DR. BABASAHEB AMBEDAKAR OPEN UNIVERSITY';
  const addressLines = [];
  let gstin = '24ABJFA1919Q1Z4';

  let invoiceNo = 'RSS/183/2026-27';
  let invoiceDate = '04/08/2026';
  let gemContractNo = 'GEMC-511687737033144';
  let generatedDate = '08/12/2025';
  let bidRaPbpNo = 'GEM/2025/B/6861369';
  let workOrderNo = 'નં. બાઆંઓયૂ/રજી/સ્ટોર્સ/3326/2025  તા.-10/12/2025';
  let sacCode = '998539';
  let billingMonthTitle = 'Bill for the month of JULY- 2026';
  let billingPeriod = '01/07/2026 TO 31/07/2026';
  let projectScope = 'The provision of sanitation and facility management services at "Dr. Babasaheb Ambedkar Open University." TOTAL MANDAYS PER DAYS = 25';

  let ratePerDay = 692.71;
  let mandays = 730.5;
  let totalAmount = 506024.66;

  let serviceChargePct = 3.85;
  let serviceChargeAmt = 19481.95;
  let cgstAmt = 45542.22;
  let sgstAmt = 45542.22;
  let consumablesAmt = 0.01;
  let machineryAmt = 0.01;
  let extraSalaryRemark = 'EXTRA SALARY 452*12%=54.24 PROVIDED BY BAOU';

  let bankName = 'THE KARNAVATI CO-OP BANK LTD.';
  let branchName = 'NIKOL ROAD';
  let accountNo = '124003012000007';
  let ifscCode = 'GSCB0UTKCBL';
  let companyGstin = '24AAOFR9854P1Z4';
  let panNo = 'AAOFR9854P';

  for (let r = 0; r < rows.length; r++) {
    const row = rows[r];
    const rowStr = row.join(' ').toUpperCase();

    if (r === 0 && row[0]) toTitle = String(row[0]).trim();
    if (r === 1 && row[0]) companyName = String(row[0]).trim();
    if ((r >= 2 && r <= 5) && row[0]) {
      const line = String(row[0]).trim();
      if (!line.toUpperCase().includes('GST NO') && !line.toUpperCase().includes('BILL FOR')) {
        addressLines.push(line);
      }
    }
    if (rowStr.includes('GST NO.')) {
      const match = rowStr.match(/GST NO\.\s*([A-Z0-9]+)/);
      if (match) gstin = match[1];
    }
    if (rowStr.includes('BILL FOR THE MONTH OF')) {
      billingMonthTitle = String(row[0] || '').trim();
    }
    if (rowStr.includes('PERIOD :-') || rowStr.includes('PERIOD:')) {
      billingPeriod = String(row[0] || '').replace(/PERIOD\s*[:-]+\s*/i, '').trim();
    }

    // Meta items
    for (let c = 0; c < row.length; c++) {
      const val = String(row[c]).trim().toUpperCase();
      const nextVal = row[c + 1] !== undefined ? String(row[c + 1]).trim() : '';

      if (val.includes('INVOICE NO.')) {
        if (nextVal) invoiceNo = nextVal;
      } else if (val.includes('INVOICE DATE:')) {
        if (nextVal) invoiceDate = formatDate(nextVal);
      } else if (val.includes('GEM CONTRACT NO.')) {
        if (nextVal) gemContractNo = nextVal;
      } else if (val.includes('GENERATED DATE')) {
        if (nextVal) generatedDate = formatDate(nextVal);
      } else if (val.includes('BID/RA/PBP NO.')) {
        if (nextVal) bidRaPbpNo = nextVal;
      } else if (val.includes('WORK ORDER NO.')) {
        if (nextVal) workOrderNo = nextVal;
      } else if (val.includes('SAC CODE:')) {
        if (nextVal) sacCode = nextVal;
      } else if (val === 'BANK NAME') {
        if (nextVal) bankName = nextVal;
      } else if (val === 'BRANCH NAME.') {
        if (nextVal) branchName = nextVal;
      } else if (val === 'BANK ACCOUNT NO.') {
        if (nextVal) accountNo = nextVal;
      } else if (val === 'IFSC CODE') {
        if (nextVal) ifscCode = nextVal;
      } else if (val === 'GSTIN') {
        if (nextVal) companyGstin = nextVal;
      } else if (val === 'PAN NO.') {
        if (nextVal) panNo = nextVal;
      }
    }

    if (rowStr.includes('THE PROVISION OF SANITATION')) {
      projectScope = String(row[1] || row[0]).trim();
    }
    if (row[1] && String(row[1]).trim().toUpperCase() === 'TOTAL' && row[2]) {
      ratePerDay = Number(row[2]) || ratePerDay;
      mandays = Number(row[3]) || mandays;
      totalAmount = Number(row[4]) || Math.round(ratePerDay * mandays * 100) / 100;
    }
    if (rowStr.includes('SERVICE CHARGE @')) {
      serviceChargeAmt = Number(row[4]) || serviceChargeAmt;
    }
    if (rowStr.includes('CGST @')) {
      cgstAmt = Number(row[4]) || cgstAmt;
    }
    if (rowStr.includes('SGST @')) {
      sgstAmt = Number(row[4]) || sgstAmt;
    }
    if (rowStr.includes('EXTRA SALARY')) {
      for (const cell of row) {
        if (String(cell).includes('EXTRA SALARY')) {
          extraSalaryRemark = String(cell).trim();
        }
      }
    }
  }

  return {
    name: fileName.replace(/\.[^/.]+$/, '') || 'BAOU Sanitation Bill',
    description: 'Imported BAOU Open University sanitation & facility format with GeM contract details.',
    formatType: 'baou_gem',
    isDefault: false,
    config: {
      formatType: 'baou_gem',
      recipient: {
        toTitle: toTitle || 'TO,',
        designation: '',
        companyName: companyName || 'DR. BABASAHEB AMBEDAKAR OPEN UNIVERSITY',
        addressLines: addressLines.length > 0 ? addressLines : [
          'Jyotirmay parisar,',
          'S.G. Highway, Chharodi,',
          'Ahmedabad,',
          'Gujarat-382481',
        ],
        gstin: gstin || '24ABJFA1919Q1Z4',
      },
      invoiceMeta: {
        invoicePrefix: invoiceNo.split('/')[0] || 'RSS',
        invoiceNo: invoiceNo || 'RSS/183/2026-27',
        invoiceDate: invoiceDate || '04/08/2026',
        gemContractNo: gemContractNo || 'GEMC-511687737033144',
        generatedDate: generatedDate || '08/12/2025',
        bidRaPbpNo: bidRaPbpNo || 'GEM/2025/B/6861369',
        workOrderNo: workOrderNo || 'નં. બાઆંઓયૂ/રજી/સ્ટોર્સ/3326/2025  તા.-10/12/2025',
        sacCode: sacCode || '998539',
        billingMonthTitle: billingMonthTitle || 'Bill for the month of JULY- 2026',
        billingPeriod: billingPeriod || '01/07/2026 TO 31/07/2026',
      },
      billingMonthTitle: billingMonthTitle || 'Bill for the month of JULY- 2026',
      projectScope: {
        code: '',
        description: projectScope,
      },
      items: [
        {
          id: '1',
          srNo: 1,
          particular: 'TOTAL',
          ratePerDay: ratePerDay,
          mandays: mandays,
          totalAmount: totalAmount,
        },
      ],
      serviceCharge: {
        isEnabled: true,
        ratePercentage: serviceChargePct,
        label: `Service charge @ ${serviceChargePct}%`,
        amount: serviceChargeAmt,
      },
      taxes: {
        cgstPercentage: 9,
        cgstAmount: cgstAmt,
        sgstPercentage: 9,
        sgstAmount: sgstAmt,
      },
      facilityCharges: [
        {
          id: 'fc-1',
          label: 'Consumables/Equipments and cleaning agents to be provided by Service Provider',
          amount: consumablesAmt,
        },
        {
          id: 'fc-2',
          label: 'Machineries to be provider by service provider',
          amount: machineryAmt,
        },
      ],
      extraSalaryRemark: extraSalaryRemark,
      roundOff: -0.07,
      companyTaxInfo: {
        gstin: companyGstin || '24AAOFR9854P1Z4',
        panNo: panNo || 'AAOFR9854P',
      },
      bankDetails: {
        bankName: bankName || 'THE KARNAVATI CO-OP BANK LTD.',
        branchName: branchName || 'NIKOL ROAD',
        accountNo: accountNo || '124003012000007',
        ifscCode: ifscCode || 'GSCB0UTKCBL',
      },
      notes: [
        '1 All Calculations are on the basis of an English calendar months.',
        '2 Subject to Ahmedabad Jurisdiction.',
      ],
      signatory: {
        companyName: 'For, Rajput Security Service',
        signatoryTitle: 'Authorised Signatory',
      },
      footer: {
        leftNotice: 'E & O.E.',
        disclaimer: "This is a computer-generated invoice doesn't require any signature.",
      },
    },
  };
}

/**
 * Parser for Version 2.0 Security Service (INVOICE.xlsx, JULY-26.xlsx)
 */
function parseSecurityV2Excel(rows, fileName) {
  let toTitle = 'To,';
  let designation = 'THE DEAN';
  let companyName = 'Dental Collage';
  const addressLines = [];
  let gstin = '24AACTA3101G1ZJ';

  let invoiceNo = 'RSS/185/2026-27';
  let invoiceDate = '05/08/2026';
  let sacCode = '998525';
  let billingMonthTitle = 'BILL FOR THE MONTH OF JULY-2026';
  let workOrderNo = 'OUT WORD NO. B-682';
  let workOrderDate = '21/03/2025';
  let tenderNo = '115/2024-25';
  let tenderIdNo = '124733';

  let bankName = 'State Bank Of India';
  let branchName = 'THAKKARBAPA NAGAR';
  let accountNo = '33850191434';
  let ifscCode = 'SBIN0011789';
  let panNo = 'AAOFR9854P';
  let companyGstin = '24AAOFR9854P1Z4';

  const items = [];
  let tableStarted = false;
  let serviceChargePct = 3.85;
  let serviceChargeAmt = 0;
  let roundOff = 0;
  let roundOffAction = 'subtract';

  for (let r = 0; r < rows.length; r++) {
    const row = rows[r];
    const rowStr = row.join(' ').toUpperCase();

    if (row[0] && String(row[0]).trim().toUpperCase() === 'TO,') {
      toTitle = String(row[0]).trim();
    }
    if (r === 1 && row[0]) designation = String(row[0]).trim();
    if (r === 2 && row[0]) companyName = String(row[0]).trim();
    if (r >= 3 && r <= 5 && row[0]) {
      const line = String(row[0]).trim();
      if (!line.toUpperCase().includes('GSTIN') && !line.toUpperCase().includes('SR.')) {
        addressLines.push(line);
      }
    }
    if (rowStr.includes('GSTIN -') || rowStr.includes('GSTIN:')) {
      const match = rowStr.match(/GSTIN\s*[-:]*\s*([A-Z0-9]+)/);
      if (match) gstin = match[1];
    }
    if (rowStr.includes('E-TENDER NO.')) {
      const matchTender = rowStr.match(/E-TENDER NO\.\s*([^T]+)TENDER\s*L?D\s*NO\.?\s*([0-9]+)/i);
      if (matchTender) {
        tenderNo = matchTender[1].trim();
        tenderIdNo = matchTender[2].trim();
      }
    }

    // Right headers
    for (let c = 0; c < row.length; c++) {
      const val = String(row[c]).trim().toUpperCase();
      const targetVal = row[c + 2] !== undefined ? String(row[c + 2]).trim() : (row[c + 1] !== undefined ? String(row[c + 1]).trim() : '');

      if (val.includes('INVOICE NO.')) {
        if (targetVal) invoiceNo = targetVal;
      } else if (val === 'DATE:') {
        if (targetVal) invoiceDate = formatDate(targetVal);
      } else if (val.includes('BILL FOR THE MONTH OF')) {
        if (targetVal) billingMonthTitle = `BILL FOR THE MONTH OF ${targetVal}`;
      } else if (val.includes('SAC CODE NO.')) {
        if (targetVal) sacCode = targetVal;
      } else if (val.includes('YOUR WORK ORDER NO')) {
        if (targetVal) {
          const woMatch = targetVal.match(/(OUT WORD NO\.[^\s]+)/i);
          if (woMatch) workOrderNo = woMatch[1];
          const dMatch = targetVal.match(/DATED:?-?\s*([0-9/]+)/i);
          if (dMatch) workOrderDate = dMatch[1];
        }
      } else if (val === 'BANK') {
        if (row[c + 1]) bankName = String(row[c + 1]).trim();
      } else if (val === 'A/C NO.') {
        if (row[c + 1]) accountNo = String(row[c + 1]).trim();
      } else if (val === 'IFSC CODE') {
        if (row[c + 1]) ifscCode = String(row[c + 1]).trim();
      } else if (val === 'BRANCH') {
        if (row[c + 1]) branchName = String(row[c + 1]).trim();
      } else if (val === 'PAN') {
        if (row[c + 1]) panNo = String(row[c + 1]).trim();
      } else if (val === 'GSTIN') {
        if (row[c + 1]) companyGstin = String(row[c + 1]).trim();
      }
    }

    // Items table
    if (rowStr.includes('SR.') && rowStr.includes('PARTICULARS') && rowStr.includes('QUANTITY')) {
      tableStarted = true;
      continue;
    }
    if (tableStarted && (rowStr.includes('TOTAL') || rowStr.includes('SERVICE CHARGE') || rowStr.includes('IN WORD'))) {
      if (rowStr.includes('SERVICE CHARGE')) {
        const amt = Number(row[5]) || 0;
        if (amt) serviceChargeAmt = amt;
      }
      if (rowStr.includes('ROUND OFF')) {
        roundOff = Number(row[5]) || 0;
      }
      if (rowStr.includes('IN WORD')) {
        tableStarted = false;
      }
    }
    if (tableStarted) {
      const sr = Number(row[0]);
      if (!isNaN(sr) && sr > 0 && row[1]) {
        const qty = Number(row[2]) || 0;
        const presentDays = Number(row[3]) || 0;
        const wage = Number(row[4]) || 0;
        const amt = Number(row[5]) || Math.round(presentDays * wage * 100) / 100;

        items.push({
          id: `item-${items.length + 1}`,
          srNo: sr,
          particular: String(row[1]).trim(),
          quantity: qty,
          mandays: presentDays,
          ratePerDay: wage,
          totalAmount: amt,
        });
      }
    }
  }

  return {
    name: fileName.replace(/\.[^/.]+$/, '') || 'Version 2.0 Security Bill',
    description: 'Imported Version 2.0 Security Service bill format with Quantity, Present Days, and Compliances.',
    formatType: 'security_v2',
    isDefault: false,
    config: {
      formatType: 'security_v2',
      recipient: {
        toTitle: toTitle || 'To,',
        designation: designation || 'THE DEAN',
        companyName: companyName || 'Dental Collage',
        addressLines: addressLines.length > 0 ? addressLines : [
          'Nr.Anupam Cinema',
          'Khokhara,',
          'Ahmedabad',
        ],
        gstin: gstin || '24AACTA3101G1ZJ',
      },
      invoiceMeta: {
        invoicePrefix: invoiceNo.split('/')[0] || 'RSS',
        invoiceNo: invoiceNo || 'RSS/185/2026-27',
        invoiceDate: invoiceDate || '05/08/2026',
        sacCode: sacCode || '998525',
        workOrderNo: workOrderNo || 'OUT WORD NO. B-682',
        workOrderDate: workOrderDate || '21/03/2025',
        tenderNo: tenderNo || '115/2024-25',
        tenderIdNo: tenderIdNo || '124733',
        billingMonthTitle: billingMonthTitle || 'BILL FOR THE MONTH OF JULY-2026',
      },
      billingMonthTitle: billingMonthTitle || 'BILL FOR THE MONTH OF JULY-2026',
      items: items.length > 0 ? items : [
        {
          id: '1',
          srNo: 1,
          particular: 'Providing Security Service (VERSION 2.0) – Unarmed Security Guard (MALE/FEMALE)',
          quantity: 40,
          mandays: 1237,
          ratePerDay: 664.09,
          totalAmount: 821479.33,
        },
      ],
      serviceCharge: {
        isEnabled: true,
        ratePercentage: serviceChargePct,
        label: `Service Charge ${serviceChargePct}%`,
        amount: serviceChargeAmt || 31626.95,
      },
      taxes: {
        sgstPercentage: 9,
        sgstAmount: 0,
        cgstPercentage: 9,
        cgstAmount: 0,
      },
      roundOff: roundOff || 0.28,
      roundOffAction: roundOffAction,
      rcmNotice: {
        isEnabled: true,
        text: 'As Per Government Notification No. 29/2018- Central Tax (Rate) Dated 31-12-2018 RCM (Reverse Charge Mechanism) Introduced Effective From 01-01-2019. Hence, GST Liability Is Of Service Are Recipient, Now.',
        amount: 0,
      },
      wageBreakdown: {
        showBreakdown: true,
        title: 'NEW RATE APR-26',
        basic: 512.5,
        pfPercentage: 13,
        pfAmount: 66.63,
        esicPercentage: 3.25,
        esicAmount: 16.66,
        bonusPercentage: 8.33,
        bonusAmount: 42.69,
        leavePercentage: 5,
        leaveAmount: 25.63,
        totalDailyRate: 664.09,
      },
      companyTaxInfo: {
        gstin: companyGstin || '24AAOFR9854P1Z4',
        panNo: panNo || 'AAOFR9854P',
      },
      bankDetails: {
        bankName: bankName || 'State Bank Of India',
        branchName: branchName || 'THAKKARBAPA NAGAR',
        accountNo: accountNo || '33850191434',
        ifscCode: ifscCode || 'SBIN0011789',
      },
      notes: [
        '1 Subject to Ahmedabad Jurisdiction.',
        '2 This bill is to be paid by 7th of currents months.',
        '3 All Calculations are on the basis of an English calendar months.',
      ],
      signatory: {
        companyName: 'For, Rajput Security service',
        signatoryTitle: 'Authorised Signatory',
      },
      footer: {
        leftNotice: 'E & O.E.',
        disclaimer: "This is a computer-generated invoice doesn't require any signature.",
      },
    },
  };
}

/**
 * Fallback Parser for Standard Manpower / Security Format
 */
function cellAfter(row, label) {
  const wanted = label.toUpperCase();
  for (let c = 0; c < row.length; c++) {
    const val = String(row[c] || '').trim().toUpperCase();
    if (!val.includes(wanted)) continue;
    for (let k = c + 1; k < row.length; k++) {
      const next = String(row[k] ?? '').trim();
      if (next) return next;
    }
  }
  return '';
}

function lastNumber(row) {
  for (let c = row.length - 1; c >= 0; c--) {
    const n = Number(String(row[c]).replace(/,/g, ''));
    if (row[c] !== '' && !Number.isNaN(n)) return n;
  }
  return 0;
}

function parseStandardExcel(rows, fileName) {
  let toTitle = 'TO,';
  let designation = '';
  let companyName = '';
  const addressLines = [];
  let gstin = '';
  let invoiceNo = '';
  let invoiceDate = '';
  let sacCode = '';
  let workOrderNo = '';
  let workOrderDate = '';
  let billingMonthTitle = '';
  let projectCode = 'A';
  let projectDescription = '';
  const items = [];
  const penalties = [];
  const additionalCharges = [];
  let serviceChargePct = 0;
  let serviceChargeLabel = '';
  let serviceChargeAmt = 0;
  let rcmText = '';
  let bankName = '';
  let branchName = '';
  let accountNo = '';
  let ifscCode = '';
  let panNo = '';
  let companyGstin = '';
  const notes = [];
  let signatoryCompany = 'For, Rajput Security service';
  let tableStarted = false;
  let seenSubTotal = false;

  rows.forEach((row, r) => {
    const rowStr = row.map((c) => String(c || '')).join(' ').trim();
    const upper = rowStr.toUpperCase();
    if (!rowStr) return;

    if (upper.startsWith('TO,') || upper === 'TO') toTitle = String(row[0] || 'TO,').trim();
    const invoice = cellAfter(row, 'INVOICE NO');
    if (invoice) invoiceNo = invoice;
    const date = cellAfter(row, 'DATE');
    if (date && !upper.includes('WORK ORDER') && !upper.includes('GENERATED')) invoiceDate = formatDate(date);
    const sac = cellAfter(row, 'SAC');
    if (sac) sacCode = sac.replace(/[:\s]/g, '');
    if (upper.includes('WORK ORDER')) {
      const raw = cellAfter(row, 'WORK ORDER') || rowStr;
      workOrderNo = raw.replace(/^WORK ORDER[:\s-]*/i, '').trim();
      const dated = raw.match(/DATED?:?\s*-?\s*([0-9]{1,2}[-/][0-9]{1,2}[-/][0-9]{2,4})/i);
      if (dated) workOrderDate = dated[1];
    }
    if (upper.includes('BILL FOR THE MONTH')) billingMonthTitle = String(row[0] || rowStr).trim();
    if (upper.includes('GST NO') || upper.includes('GSTIN')) {
      const match = upper.match(/(?:GST(?:IN)?\s*(?:NO\.?)?\s*[-:]?\s*)([0-9A-Z]{15})/);
      if (match && !gstin && r < 12) gstin = match[1];
      if (match && r >= 12 && !companyGstin) companyGstin = match[1];
    }

    if (!tableStarted && upper.includes('PARTICULARS') && (upper.includes('MANDAY') || upper.includes('RATES PER DAY'))) {
      tableStarted = true;
      return;
    }

    if (tableStarted && !seenSubTotal) {
      const sr = Number(row[0]);
      const particular = String(row[1] || '').trim();
      if (!Number.isNaN(sr) && sr > 0 && particular) {
        const rate = Number(row[2]) || 0;
        const mandays = Number(row[3]) || 0;
        const amount = Number(row[4]) || lastNumber(row);
        items.push({
          id: `item-${items.length + 1}`,
          srNo: sr,
          particular,
          ratePerDay: rate,
          mandays,
          totalAmount: amount,
        });
        return;
      }
      if (particular && !projectDescription && /[A-Z]/.test(String(row[0] || '')) && String(row[0]).trim().length <= 3) {
        projectCode = String(row[0]).trim();
        projectDescription = particular;
        return;
      }
    }

    if (upper.includes('SUB TOTAL')) {
      seenSubTotal = true;
      return;
    }
    if (upper.includes('PENALTY')) {
      penalties.push({ id: `pen-${penalties.length + 1}`, label: String(row[1] || 'Penalty(-)').trim(), amount: lastNumber(row) });
      return;
    }
    if (upper.includes('SERVICE CHARGE')) {
      serviceChargeLabel = String(row[1] || rowStr).trim();
      const pct = serviceChargeLabel.match(/([0-9]+(?:\.[0-9]+)?)\s*%/);
      serviceChargePct = pct ? Number(pct[1]) : serviceChargePct;
      serviceChargeAmt = lastNumber(row);
      return;
    }
    if (upper.includes('REVERSE CHARGE') || upper.includes('RCM')) {
      rcmText = String(row[0] || rowStr).trim();
      return;
    }
    if (seenSubTotal && !upper.includes('TOTAL') && !upper.includes('GRAND') && !upper.includes('IN WORD') && String(row[1] || '').trim()) {
      const label = String(row[1]).trim();
      if (!/^(BANK|PAN|GST|NOTE|FOR,)/i.test(label)) {
        additionalCharges.push({ id: `add-${additionalCharges.length + 1}`, label, amount: lastNumber(row) });
      }
    }

    const bank = cellAfter(row, 'BANK NAME') || (upper.includes('BANK') && !upper.includes('BANK DETAILS') ? cellAfter(row, 'BANK') : '');
    if (bank && !upper.includes('ACCOUNT')) bankName = bank;
    const branch = cellAfter(row, 'BRANCH');
    if (branch) branchName = branch;
    const account = cellAfter(row, 'ACCOUNT') || cellAfter(row, 'A/C');
    if (account) accountNo = account;
    const ifsc = cellAfter(row, 'IFSC');
    if (ifsc) ifscCode = ifsc;
    const pan = cellAfter(row, 'PAN');
    if (pan && pan.length <= 12) panNo = pan;
    if (upper.startsWith('FOR,')) signatoryCompany = String(row.find((c) => String(c || '').trim()) || signatoryCompany).trim();
    if (/^\d+$/.test(String(row[0] || '').trim()) && String(row[1] || '').trim() && !tableStarted) {
      notes.push(String(row[1]).trim());
    }
  });

  const headerLines = [];
  for (let r = 0; r < Math.min(rows.length, 12); r++) {
    const line = String(rows[r]?.[0] || '').trim();
    const upper = line.toUpperCase();
    if (!line || upper.startsWith('TO') || upper.includes('GST') || upper.includes('BILL FOR') || upper.includes('SR.')) continue;
    headerLines.push(line);
  }
  if (headerLines[0]) designation = headerLines[0];
  if (headerLines[1]) companyName = headerLines[1];
  addressLines.push(...headerLines.slice(2).filter((line) => !line.toUpperCase().includes('PARTICULAR')));

  if (!companyName || items.length === 0) {
    throw new Error('This Excel file does not match a known bill layout. Use a Standard, Security 2.0, GeM, or SVNIT invoice.');
  }

  return {
    name: companyName || fileName.replace(/\.[^/.]+$/, '') || 'Imported Security Bill',
    description: `Imported from ${fileName}`,
    formatType: 'standard',
    isDefault: false,
    config: {
      formatType: 'standard',
      letterhead: {
        enabled: true,
        mode: 'digital',
        topMarginMm: 38,
        bottomMarginMm: 26,
        sideMarginMm: 8,
        showWatermark: true,
        opacity: 100,
      },
      recipient: {
        toTitle: toTitle || 'TO,',
        designation: designation || '',
        companyName,
        addressLines,
        gstin,
      },
      invoiceMeta: {
        invoicePrefix: (invoiceNo.split('/')[0] || 'RSS'),
        invoiceNo: invoiceNo || 'RSS/00/2026-27',
        invoiceDate: invoiceDate || '',
        sacCode: sacCode || '',
        workOrderNo,
        workOrderDate,
      },
      billingMonthTitle: billingMonthTitle || '',
      projectScope: {
        code: projectCode || 'A',
        description: projectDescription || '',
      },
      items,
      penalties: penalties.length > 0 ? penalties : [{ id: 'pen-1', label: 'Penalty(-)', amount: 0 }],
      additionalCharges,
      serviceCharge: {
        isEnabled: serviceChargePct > 0 || serviceChargeAmt > 0,
        ratePercentage: serviceChargePct || 0,
        label: serviceChargeLabel || 'SERVICE CHARGE',
        amount: serviceChargeAmt,
      },
      rcmNotice: {
        isEnabled: Boolean(rcmText),
        text: rcmText,
        amount: 0,
      },
      companyTaxInfo: {
        gstin: companyGstin,
        panNo,
      },
      bankDetails: {
        bankName,
        branchName,
        accountNo,
        ifscCode,
      },
      notes,
      signatory: {
        companyName: signatoryCompany,
        signatoryTitle: 'Authorised Signatory',
      },
      footer: {
        leftNotice: 'E & O.E.',
        disclaimer: "This is a computer-generated invoice doesn't require any signature.",
      },
    },
  };
}

/**
 * Date helper
 */
function formatDate(val) {
  if (!val) return '';
  if (val instanceof Date) {
    const day = String(val.getDate()).padStart(2, '0');
    const month = String(val.getMonth() + 1).padStart(2, '0');
    const year = val.getFullYear();
    return `${day}/${month}/${year}`;
  }
  const str = String(val).trim();
  // Check ISO string
  if (str.includes('T')) {
    const d = new Date(str);
    if (!isNaN(d.getTime())) {
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      return `${day}/${month}/${year}`;
    }
  }
  return str;
}

/**
 * EXPORT BILL TO EXCEL (.xlsx)
 * Generates an Excel file matching the formatType layout
 */
export function exportBillToExcel(currentTemplate) {
  if (!currentTemplate) return;
  const config = currentTemplate.config || {};
  const formatType = config.formatType || currentTemplate.formatType || 'standard';

  const wb = XLSX.utils.book_new();
  const wsData = [];

  // Helper row push
  const addRow = (arr) => wsData.push(arr);

  if (formatType === 'svnit_frequency') {
    // SVNIT 7-Column Layout
    addRow(['To,', '', '', 'Invoice No.', '', '', config.invoiceMeta?.invoiceNo || 'RSS/000/2026-27']);
    addRow([config.recipient?.designation || 'The Director,', '', '', 'Date:', '', '', config.invoiceMeta?.invoiceDate || '']);
    addRow([config.recipient?.companyName || 'SVNIT SURAT', '', '', 'Contract No.', '', '', config.invoiceMeta?.contractNo || '']);
    addRow([config.recipient?.addressLines?.[0] || '', '', '', 'Generated Date:', '', '', config.invoiceMeta?.generatedDate || '']);
    addRow([config.recipient?.addressLines?.[1] || '', '', '', 'SAC Code No.', '', '', config.invoiceMeta?.sacCode || '9985']);
    addRow(['', '', '', 'Billing Month', '', '', config.billingMonthTitle || '']);
    addRow([`GSTIN: ${config.recipient?.gstin || ''}`, '', '', 'Billing Period', '', '', config.invoiceMeta?.billingPeriod || '']);
    addRow([config.recipient?.workDescription || 'Work: Housekeeping, Sanitation and disinfection services at SVNIT Campus, Surat']);
    addRow([]);
    addRow(['SR. NO.', 'Description', 'Quantity', 'Unit Price', 'Frequency', 'No. of Working Days', 'Amount [C*D*E=F]']);
    addRow(['A', 'B', 'C', 'D', 'E', 'F', 'G']);

    let totalAmt = 0;
    (config.items || []).forEach((item, idx) => {
      const amt = Number(item.totalAmount) || 0;
      totalAmt += amt;
      addRow([idx + 1, item.particular, item.quantity, item.unitPrice, item.frequency, item.workingDays, amt]);
    });

    const withoutGst = Math.round(totalAmt * (config.reverseGstFactor || 0.847457627118644) * 100) / 100;
    const sgst = Math.round(withoutGst * 0.09 * 100) / 100;
    const cgst = Math.round(withoutGst * 0.09 * 100) / 100;
    const totalGst = sgst + cgst;
    const grandTotal = withoutGst + totalGst;

    addRow(['Total Amount (Rs.)', '', '', '', '', '', totalAmt]);
    addRow(['TOTAL AMOUNT WITHOUT GST (Rs.)', '', '', '', '', '', withoutGst]);
    addRow(['SGST @ 9%', '', '', '', '', '', sgst]);
    addRow(['CGST @ 9%', '', '', '', '', '', cgst]);
    addRow(['Add total GST Amount (Rs.)', '', '', '', '', '', totalGst]);
    addRow(['Grand Total (Rs.)', '', '', '', '', '', grandTotal]);
    addRow(['IN WORDS', numberToIndianWords(grandTotal), '', '', 'Total Payables', '', grandTotal]);
    addRow([]);
    addRow(['', '', '', 'BANK DETAILS:']);
    addRow(['PAN', config.companyTaxInfo?.panNo || '', '', 'BANK', config.bankDetails?.bankName || '']);
    addRow(['GSTIN', config.companyTaxInfo?.gstin || '', '', 'A/C NO.', config.bankDetails?.accountNo || '']);
    addRow(['', '', '', 'IFSC CODE', config.bankDetails?.ifscCode || '']);
    addRow(['Note:', '', '', 'BRANCH', config.bankDetails?.branchName || '']);
    (config.notes || []).forEach((note, idx) => {
      addRow([idx + 1, note, '', '', '', '', idx === 0 ? (config.signatory?.companyName || 'For, Rajput Security service') : '']);
    });
    addRow(['E & O.E.', '', '', '', '', '', config.signatory?.signatoryTitle || 'Authorised Signatory']);

  } else if (formatType === 'baou_gem') {
    // BAOU GeM Facility Layout
    addRow(['TO,', '', '', 'Invoice No.', config.invoiceMeta?.invoiceNo || 'RSS/183/2026-27']);
    addRow([config.recipient?.companyName || 'DR. BABASAHEB AMBEDAKAR OPEN UNIVERSITY']);
    addRow([config.recipient?.addressLines?.[0] || '', '', '', 'Invoice Date:', config.invoiceMeta?.invoiceDate || '']);
    addRow([config.recipient?.addressLines?.[1] || '', '', '', 'Gem Contract No.', config.invoiceMeta?.gemContractNo || '']);
    addRow([config.recipient?.addressLines?.[2] || '', '', '', 'Generated Date :', config.invoiceMeta?.generatedDate || '']);
    addRow([config.recipient?.addressLines?.[3] || '', '', '', 'Bid/RA/PBP No.:', config.invoiceMeta?.bidRaPbpNo || '']);
    addRow([`GST NO. ${config.recipient?.gstin || ''}`, '', '', 'Work Order No.', config.invoiceMeta?.workOrderNo || '']);
    addRow([config.billingMonthTitle || 'Bill for the month of JULY- 2026', '', '', 'SAC CODE:', config.invoiceMeta?.sacCode || '998539']);
    addRow([`Period :- ${config.invoiceMeta?.billingPeriod || ''}`]);
    addRow([]);
    addRow(['', 'Particulars', 'Rates per day', 'Total Mandays in Month', 'Total Amount']);
    addRow(['', '', 'A', 'B', 'C']);
    addRow(['', config.projectScope?.description || 'The provision of sanitation and facility management services']);
    
    let subTotal = 0;
    (config.items || []).forEach((item) => {
      subTotal += Number(item.totalAmount) || 0;
      addRow(['', item.particular, item.ratePerDay, item.mandays, item.totalAmount]);
    });

    const svcCharge = Math.round(subTotal * ((config.serviceCharge?.ratePercentage || 3.85) / 100) * 100) / 100;
    const cgst = Math.round(subTotal * 0.09 * 100) / 100;
    const sgst = cgst;
    const totalTaxed = Math.round((subTotal + svcCharge + cgst + sgst) * 100) / 100;
    let facilityTotal = 0;
    (config.facilityCharges || []).forEach((fc) => {
      facilityTotal += Number(fc.amount) || 0;
    });
    const grandTotal = Math.round(totalTaxed + facilityTotal);

    addRow(['', '', 'Service charge @ 3.85%', '', svcCharge]);
    addRow(['', '', 'CGST @ 9%', '', cgst]);
    addRow(['', '', 'SGST @9%', '', sgst]);
    addRow(['', '', 'TOTAL', '', totalTaxed]);
    (config.facilityCharges || []).forEach((fc) => {
      addRow(['', fc.label, '', '', fc.amount]);
    });
    addRow(['', 'G.TOTAL', '', '', grandTotal, '', config.extraSalaryRemark || '']);
    addRow(['In Words', numberToIndianWords(grandTotal), '', '', grandTotal]);
    addRow([]);
    addRow(['', 'GSTIN', config.companyTaxInfo?.gstin || '']);
    addRow(['', 'PAN NO.', config.companyTaxInfo?.panNo || '']);
    addRow(['', 'Bank Details:']);
    addRow(['', 'Bank Name', config.bankDetails?.bankName || '']);
    addRow(['', 'Branch Name.', config.bankDetails?.branchName || '']);
    addRow(['', 'Bank Account No.', config.bankDetails?.accountNo || '']);
    addRow(['', 'IFSC Code', config.bankDetails?.ifscCode || '']);
    addRow([]);
    addRow(['Note:']);
    (config.notes || []).forEach((n, idx) => {
      addRow([idx + 1, n, '', '', config.signatory?.companyName || 'For, Rajput Security Service']);
    });
    addRow(['', '', '', '', config.signatory?.signatoryTitle || 'Authorised Signatory']);
    addRow(['E & O.E.']);
    addRow([config.footer?.disclaimer || "This is a computer-generated invoice doesn't require any signature."]);

  } else if (formatType === 'security_v2') {
    // Version 2.0 Security Layout (Dental College / AMC MET)
    addRow(['To,', '', 'Invoice No.', '', config.invoiceMeta?.invoiceNo || 'RSS/185/2026-27']);
    addRow([config.recipient?.designation || '', '', 'Date:', '', config.invoiceMeta?.invoiceDate || '']);
    addRow([config.recipient?.companyName || '', '', 'BILL FOR THE MONTH OF', '', config.billingMonthTitle || '']);
    addRow([config.recipient?.addressLines?.[0] || '', '', 'SAC Code No.', '', config.invoiceMeta?.sacCode || '998525']);
    addRow([config.recipient?.addressLines?.[1] || '', '', 'Your Work Order No', '', `${config.invoiceMeta?.workOrderNo || ''} DATED:- ${config.invoiceMeta?.workOrderDate || ''}`]);
    addRow([config.recipient?.addressLines?.[2] || '']);
    addRow([`GSTIN - ${config.recipient?.gstin || ''}`, '', `E-Tender No. ${config.invoiceMeta?.tenderNo || ''} Tender ID No.${config.invoiceMeta?.tenderIdNo || ''}`]);
    addRow([]);
    addRow(['SR. NO.', 'PARTICULARS', 'Quantity', 'Total No. of Present days in a month', 'Minimum Daily wages with all Compliances', 'Amount']);
    addRow(['A', 'B', 'C', 'D', 'E', 'F']);

    let subTotal = 0;
    (config.items || []).forEach((item, idx) => {
      subTotal += Number(item.totalAmount) || 0;
      addRow([idx + 1, item.particular, item.quantity, item.mandays, item.ratePerDay, item.totalAmount]);
    });

    const svcCharge = Math.round(subTotal * ((config.serviceCharge?.ratePercentage || 3.85) / 100) * 100) / 100;
    const roundOff = Number(config.roundOff) || 0;
    const isSub = config.roundOffAction === 'subtract';
    const grandTotal = Math.round((subTotal + svcCharge + (isSub ? -roundOff : roundOff)) * 100) / 100;

    addRow(['', '', '', 'TOTAL', '', subTotal]);
    addRow([config.rcmNotice?.text || '', '', '', `Service Charge ${config.serviceCharge?.ratePercentage || 3.85}%`, '', svcCharge]);
    addRow(['', '', '', 'SGST @ 9%', '', 0]);
    addRow(['', '', '', 'CGST @ 9%', '', 0]);
    addRow(['', '', '', `ROUND OFF (${isSub ? '-' : '+'})`, '', roundOff]);
    addRow(['IN WORD', numberToIndianWords(grandTotal), '', '', '', grandTotal]);
    addRow([]);
    addRow(['PAN', config.companyTaxInfo?.panNo || '', '', 'BANK DETAILS:']);
    addRow(['GSTIN', config.companyTaxInfo?.gstin || '', '', 'BANK', config.bankDetails?.bankName || '']);
    addRow(['', '', '', 'A/C NO.', config.bankDetails?.accountNo || '']);
    addRow(['', '', '', 'IFSC CODE', config.bankDetails?.ifscCode || '']);
    addRow(['Note:', '', '', 'BRANCH', config.bankDetails?.branchName || '']);
    (config.notes || []).forEach((n, idx) => {
      addRow([idx + 1, n, '', '', '', idx === 1 ? (config.signatory?.companyName || 'For, Rajput Security service') : '']);
    });
    addRow(['E & O.E.', '', '', '', '', config.signatory?.signatoryTitle || 'Authorised Signatory']);

    if (config.wageBreakdown?.showBreakdown) {
      addRow([]);
      addRow(['', config.wageBreakdown.title || 'NEW RATE APR-26']);
      addRow(['', 'MINIMUM WAGES', config.wageBreakdown.basic]);
      addRow(['', 'PROVIDENT FUND @13%', config.wageBreakdown.pfAmount]);
      addRow(['', 'ESIC @3.25%', config.wageBreakdown.esicAmount]);
      addRow(['', 'BONUS @8.33%', config.wageBreakdown.bonusAmount]);
      addRow(['', 'LEAVE @5%', config.wageBreakdown.leaveAmount]);
      addRow(['', 'TOTAL', config.wageBreakdown.totalDailyRate]);
    }

  } else {
    // Standard Format (SRFDCL)
    addRow(['TO,', '', '', '', 'Invoice No.:', config.invoiceMeta?.invoiceNo || 'RSS/00/2026-27']);
    addRow([config.recipient?.designation || '', '', '', '', 'Date:', config.invoiceMeta?.invoiceDate || '']);
    addRow([config.recipient?.companyName || '', '', '', '', 'SAC CODE:', config.invoiceMeta?.sacCode || '998529']);
    (config.recipient?.addressLines || []).forEach((line) => {
      addRow([line]);
    });
    addRow([`GST NO. ${config.recipient?.gstin || ''}`, '', '', '', `Work order: ${config.invoiceMeta?.workOrderNo || ''}`]);
    addRow([]);
    addRow([config.billingMonthTitle || 'BILL FOR THE MONTH OF MARCH-2026']);
    addRow(['Sr. No.', 'Particulars', 'Rates per day', 'Total Mandays in Month', 'Total Amount']);
    addRow(['', '', 'A', 'B', 'C']);
    addRow([config.projectScope?.code || 'A', config.projectScope?.description || 'Providing round the clock security service']);

    let subTotal = 0;
    (config.items || []).forEach((item, idx) => {
      subTotal += Number(item.totalAmount) || 0;
      addRow([idx + 1, item.particular, item.ratePerDay, item.mandays, item.totalAmount]);
    });

    let penaltyTotal = 0;
    (config.penalties || []).forEach((p) => {
      penaltyTotal += Number(p.amount) || 0;
    });
    const afterPenalty = Math.max(0, subTotal - penaltyTotal);

    let addCharges = 0;
    (config.additionalCharges || []).forEach((c) => {
      addCharges += Number(c.amount) || 0;
    });
    const afterAdd = afterPenalty + addCharges;
    const svcCharge = Math.round(afterAdd * ((config.serviceCharge?.ratePercentage || 1.01) / 100) * 100) / 100;
    const grandTotal = Math.round((afterAdd + svcCharge) * 100) / 100;

    addRow(['', 'SUB TOTAL', '', '', subTotal]);
    (config.penalties || []).forEach((p) => {
      addRow(['', p.label, '', '', p.amount]);
    });
    if (penaltyTotal > 0) addRow(['', 'TOTAL', '', '', afterPenalty]);
    (config.additionalCharges || []).forEach((c) => {
      addRow(['', c.label, '', '', c.amount]);
    });
    if (addCharges > 0) addRow(['', 'TOTAL', '', '', afterAdd]);
    if (config.serviceCharge?.isEnabled) {
      addRow(['', config.serviceCharge.label || `SERVICE CHARGE @${config.serviceCharge.ratePercentage}%`, '', '', svcCharge]);
    }
    if (config.rcmNotice?.isEnabled) {
      addRow([config.rcmNotice.text, '', '', '', '-']);
    }
    addRow(['', 'GRAND TOTAL', '', '', grandTotal]);
    addRow(['In Words', numberToIndianWords(grandTotal), '', '', grandTotal]);
    addRow([]);
    addRow(['GSTIN', config.companyTaxInfo?.gstin || '', '', config.signatory?.companyName || 'For, Rajput Security Service']);
    addRow(['PAN NO.', config.companyTaxInfo?.panNo || '']);
    addRow(['Bank Details:']);
    addRow(['Bank Name', config.bankDetails?.bankName || '']);
    addRow(['Branch Name', config.bankDetails?.branchName || '']);
    addRow(['Bank Account No.', config.bankDetails?.accountNo || '']);
    addRow(['IFSC Code', config.bankDetails?.ifscCode || '', '', config.signatory?.signatoryTitle || 'Authorised Signatory']);
    addRow([]);
    addRow(['Note:']);
    (config.notes || []).forEach((n, idx) => {
      addRow([idx + 1, n]);
    });
    addRow(['E & O.E.', config.footer?.disclaimer || '']);
  }

  const ws = XLSX.utils.aoa_to_sheet(wsData);

  // Set column widths
  ws['!cols'] = [
    { wch: 8 },
    { wch: 45 },
    { wch: 16 },
    { wch: 22 },
    { wch: 18 },
    { wch: 18 },
    { wch: 20 },
  ];

  XLSX.utils.book_append_sheet(wb, ws, 'INVOICE');
  const safeName = (currentTemplate.name || 'Invoice').replace(/[^a-zA-Z0-9_-]/g, '_');
  XLSX.writeFile(wb, `${safeName}.xlsx`);
}
